// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import styles from '@app/features/premium/components/experience/ShopierAddTimeModal.module.css';
import {
	formatMoney,
	parseAmount,
	startShopierCheckout,
} from '@app/features/premium/components/experience/ShopierCheckoutActions';
import {SHOPIER_COPY} from '@app/features/premium/utils/ShopierCopy';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import type {ShopierOverviewResponse, ShopierPlanSchema} from '@fluxer/schema/src/domains/premium/ShopierSchemas';
import {useLingui} from '@lingui/react/macro';
import {CrownIcon, LightningIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

interface ShopierAddTimeModalProps {
	plans: ShopierOverviewResponse['plans'];
	initialPlan: ShopierPlanSchema;
	onFinished: () => void;
}

export const ShopierAddTimeModal = observer(({plans, initialPlan, onFinished}: ShopierAddTimeModalProps) => {
	const {i18n} = useLingui();
	const [selected, setSelected] = useState<ShopierPlanSchema>(initialPlan);
	const nameArgs = {premiumProductName: PREMIUM_PRODUCT_NAME};

	const handleContinue = () => {
		ModalCommands.pop();
		startShopierCheckout(selected, onFinished);
	};

	return (
		<Modal.Root size="small" centered data-flx="premium.shopier-add-time.modal-root">
			<Modal.Header title={i18n._(SHOPIER_COPY.chooserTitle, nameArgs)} data-flx="premium.shopier-add-time.header" />
			<Modal.Content data-flx="premium.shopier-add-time.content">
				<Modal.ContentLayout>
					<p className={styles.body}>{i18n._(SHOPIER_COPY.chooserBody)}</p>
					<div className={styles.options} role="radiogroup" aria-label={i18n._(SHOPIER_COPY.chooserTitle, nameArgs)}>
						{plans.map((plan) => {
							const isYearly = plan.plan === 'yearly';
							const amount = parseAmount(plan.price);
							const isSelected = selected === plan.plan;
							return (
								<button
									key={plan.plan}
									type="button"
									role="radio"
									aria-checked={isSelected}
									className={clsx(styles.option, isSelected && styles.optionSelected)}
									onClick={() => setSelected(plan.plan)}
									data-flx={`premium.shopier-add-time.option.${plan.plan}`}
								>
									<span className={styles.optionIcon}>
										{isYearly ? <CrownIcon weight="fill" size={18} /> : <LightningIcon weight="fill" size={18} />}
									</span>
									<span className={styles.optionText}>
										<span className={styles.optionTitle}>
											{isYearly ? i18n._(SHOPIER_COPY.yearlyTitle) : i18n._(SHOPIER_COPY.monthlyTitle)}
										</span>
										{isYearly && <span className={styles.optionBadge}>{i18n._(SHOPIER_COPY.bestValue)}</span>}
									</span>
									<span className={styles.optionPrice}>
										{amount ? formatMoney(amount, plan.currency, i18n.locale) : i18n._(SHOPIER_COPY.priceOnShopier)}
									</span>
									<span className={styles.radio} aria-hidden="true" />
								</button>
							);
						})}
					</div>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer data-flx="premium.shopier-add-time.footer">
				<Button variant="secondary" onClick={() => ModalCommands.pop()} data-flx="premium.shopier-add-time.cancel">
					{i18n._(SHOPIER_COPY.modalClose)}
				</Button>
				<Button variant="primary" onClick={handleContinue} data-flx="premium.shopier-add-time.continue">
					{i18n._(SHOPIER_COPY.chooserContinue)}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});

export function openShopierAddTime(
	plans: ShopierOverviewResponse['plans'],
	initialPlan: ShopierPlanSchema,
	onFinished: () => void,
): void {
	ModalCommands.push(
		modal(() => <ShopierAddTimeModal plans={plans} initialPlan={initialPlan} onFinished={onFinished} />),
	);
}
